import { useTheme } from '@mui/material/styles';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import AvatarChip from '../../../../shared/avatar-chip';
import {
  generalFieldMergeTags,
  triggerEventOptions,
} from '../trigger-event-options';
import { isTemplateSelectableInRulesEngine } from './send-email-template-action.utils';
import { Editor } from 'tinymce';
import DistractionFreeEditor from '../../../../shared/editor/distraction-free-editor';
import MergeTagOnlyEditor from '../../../../shared/editor/mergetag-only-editor';
import { RulesEngineFormValues } from '../rules-engine-form-page';
import { getWelcomeTemplates, UserKeys } from '../../../../api/users-api';
import { Messages } from '../../../../utils/message-constants';
import { useQuery } from '@tanstack/react-query';
import SharedDialog from '../../../../shared/shared-dialog';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import ActionDescription from './action-description';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

interface SendEmailTemplateActionProps {
  description?: string;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  control: Control<RulesEngineFormValues>;
  watch: UseFormWatch<RulesEngineFormValues>;
  trigger: UseFormTrigger<RulesEngineFormValues>;
  clearErrors: UseFormClearErrors<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
  onCreateEmailTemplate: () => void;
  isSavingDraft: boolean;
  autoSelectEmailTemplateId?: number;
  emailSubjectFromState?: string;
  emailMessageFromState?: string;
}

export default function SendEmailTemplateAction({
  setValue,
  control,
  watch,
  trigger,
  errors,
  clearErrors,
  onCreateEmailTemplate,
  isSavingDraft,
  autoSelectEmailTemplateId,
  emailSubjectFromState,
  emailMessageFromState,
  description,
}: SendEmailTemplateActionProps) {
  const emailNotificatinKey = useRef(new Date().getTime().toString());
  const ccEmailNotificatinKey = useRef(new Date().getTime().toString());
  const bccEmailNotificatinKey = useRef(new Date().getTime().toString());
  const [addedEmail, setAddedEmail] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [addedCcEmail, setAddedCcEmail] = useState('');
  const [addedBccEmail, setAddedBccEmail] = useState('');
  const theme = useTheme();

  const emailNotificationEmails = watch('emailInfo.emailNotificationEmails');
  const ccEmails = watch('emailInfo.ccEmails');
  const bccEmails = watch('emailInfo.bccEmails');

  const htmlBody = watch('emailInfo.emailMessage');
  const emailSubject = watch('emailInfo.emailSubject');
  const triggerEvent = watch('triggerInfo.triggerEvent');

  const currentTriggerEvent = triggerEventOptions.find(
    (option) => option.value === triggerEvent
  );

  const { data: emailTemplates, isLoading: isLoadingEmailTemplates } = useQuery(
    {
      queryKey: [UserKeys.EmailTemplates],
      queryFn: () => getWelcomeTemplates(),
      meta: {
        errorMessage: Messages.Errors.Network.Loading('welcome templates'),
      },
    }
  );

  const editorRef = useRef<Editor | null>(null);
  const editorSubjectRef = useRef<Editor | null>(null);

  const { administrationRoles } = useUserSettings();
  const canCreateEmailTemplate = isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.CreateWelcomeTemplates.value
  );

  function setHtmlBody() {
    if (editorRef.current) {
      setValue('emailInfo.emailMessage', editorRef.current.getContent(), {
        shouldDirty: true,
      });
    }
  }

  function setSubject() {
    if (editorSubjectRef.current) {
      setValue(
        'emailInfo.emailSubject',
        editorSubjectRef.current.getContent(),
        {
          shouldDirty: true,
        }
      );
    }
  }

  const mergeTags = [
    generalFieldMergeTags,
    {
      title: 'Event Specific Fields',
      menu: currentTriggerEvent?.availableParameters.map((param) => {
        return {
          value: param.value,
          title: param.displayValue,
        };
      }),
    },
  ];

  const allEmailTemplates = useMemo(() => {
    return emailTemplates?.results || [];
  }, [emailTemplates?.results]);

  const autoSelectedTemplate = useMemo(() => {
    if (!autoSelectEmailTemplateId || !allEmailTemplates.length) return null;
    const matchingTemplate = allEmailTemplates.find(
      (t) => t.id === autoSelectEmailTemplateId
    );
    return matchingTemplate &&
      isTemplateSelectableInRulesEngine(matchingTemplate)
      ? matchingTemplate
      : null;
  }, [autoSelectEmailTemplateId, allEmailTemplates]);

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    autoSelectedTemplate?.id || autoSelectEmailTemplateId || null
  );

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return;

    if (autoSelectedTemplate) {
      setValue('emailInfo.emailSubject', autoSelectedTemplate.templateSubject, {
        shouldDirty: true,
      });
      setValue('emailInfo.emailMessage', autoSelectedTemplate.templateHtml, {
        shouldDirty: true,
      });
      hasInitialized.current = true;
    } else if (emailSubjectFromState && emailMessageFromState) {
      setValue('emailInfo.emailSubject', emailSubjectFromState);
      setValue('emailInfo.emailMessage', emailMessageFromState);
      hasInitialized.current = true;
    }
  }, [
    autoSelectedTemplate,
    emailSubjectFromState,
    emailMessageFromState,
    setValue,
  ]);

  return (
    <Stack gap={1} width={'100%'}>
      <ActionDescription description={description} />
      <SharedDialog
        open={confirmDialogOpen}
        handleClose={() => setConfirmDialogOpen(false)}
        title="Leave to Create Template"
        dialogContent={
          <Typography variant="body1">
            To create a new welcome template, we'll need to take you to the
            Welcome Template Creator. Your current rule will be saved as a
            draft.
          </Typography>
        }
        primaryButton={{
          title: 'Save & Go to Template Creation',
          action: () => {
            onCreateEmailTemplate();
          },
          loading: isSavingDraft,
          disabled: isSavingDraft,
        }}
      />
      {isLoadingEmailTemplates ? (
        <Skeleton variant="rounded" width={400} height={56} />
      ) : (
        <Box sx={{ width: 400 }}>
          <Typography sx={{ fontWeight: 'bold', mt: 2 }}>
            Email Template
          </Typography>
          <Autocomplete
            sx={{ mt: 1 }}
            options={allEmailTemplates}
            value={
              selectedTemplateId
                ? allEmailTemplates.find((t) => t.id === selectedTemplateId) ??
                  null
                : null
            }
            getOptionLabel={(option) => option.templateName}
            getOptionDisabled={(option) =>
              !isTemplateSelectableInRulesEngine(option)
            }
            slotProps={{
              listbox: {
                sx: {
                  '& .MuiAutocomplete-option[aria-disabled="true"]': {
                    pointerEvents: 'auto',
                  },
                },
              },
            }}
            onChange={(_, newValue) => {
              if (newValue) {
                setValue('emailInfo.emailSubject', newValue.templateSubject, {
                  shouldDirty: true,
                });
                setValue('emailInfo.emailMessage', newValue.templateHtml, {
                  shouldDirty: true,
                });
                editorSubjectRef.current?.setContent(newValue.templateSubject);
                editorRef.current?.setContent(newValue.templateHtml);
                setSelectedTemplateId(newValue.id);
              } else {
                setValue('emailInfo.emailSubject', '', {
                  shouldDirty: true,
                });
                setValue('emailInfo.emailMessage', '', {
                  shouldDirty: true,
                });
                editorSubjectRef.current?.setContent('');
                editorRef.current?.setContent('');
                setSelectedTemplateId(null);
              }
            }}
            disabled={allEmailTemplates.length === 0}
            fullWidth
            renderOption={(props, option) => {
              const isDisabled = !isTemplateSelectableInRulesEngine(option);
              return (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  onClick={
                    isDisabled
                      ? (e: React.MouseEvent) => e.stopPropagation()
                      : (props as React.HTMLAttributes<HTMLLIElement>).onClick
                  }
                >
                  <Tooltip
                    arrow
                    title={
                      isDisabled
                        ? 'Template contains passwords, it is not selectable in Rules Engine.'
                        : ''
                    }
                    placement="bottom"
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <Typography>{option.templateName}</Typography>
                    </Box>
                  </Tooltip>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  allEmailTemplates.length === 0
                    ? 'No email templates available'
                    : 'Email Template'
                }
              />
            )}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ ml: 1 }}
          >
            <Typography
              sx={{
                fontSize: theme.typography.body2.fontSize,
                color: theme.palette.text.secondary,
                mt: 2,
              }}
            >
              Don't see the template you need?
            </Typography>
            <Tooltip
              arrow
              title={
                !canCreateEmailTemplate
                  ? Messages.Errors.Permission.Acting(
                      AdministrationRoles.CreateWelcomeTemplates.name
                    )
                  : ''
              }
            >
              <span>
                <Button
                  variant="outlined"
                  sx={{ mt: 2, ml: 1 }}
                  onClick={() => setConfirmDialogOpen(true)}
                  disabled={!canCreateEmailTemplate || isSavingDraft}
                >
                  Create Template
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      )}
      <Typography sx={{ fontWeight: 'bold', mt: 2 }}>Recipient</Typography>
      <Stack flexDirection={'row'} gap={1} sx={{ mt: 1 }}>
        <Controller
          name={'emailInfo.emailNotificationEmails'}
          rules={{
            validate: (value) =>
              value.length > 0 || 'At least one recipient is required.',
          }}
          control={control}
          render={({ field }) => (
            <Box sx={{ width: 400 }}>
              <SharedAutocomplete
                sources={['users', 'groups']}
                label="To"
                autocompleteValue={addedEmail}
                setAutoCompleteValue={(val) => {
                  setAddedEmail(val);
                  if (val && !field.value?.includes(val)) {
                    const updatedUsers = [...(field.value || []), val];
                    setValue(
                      'emailInfo.emailNotificationEmails',
                      updatedUsers,
                      {
                        shouldDirty: true,
                      }
                    );
                    setAddedEmail('');
                    emailNotificatinKey.current = new Date()
                      .getTime()
                      .toString();
                    trigger('emailInfo.emailNotificationEmails');
                    clearErrors('emailInfo.emailNotificationEmails');
                  }
                }}
                errors={!!errors.emailInfo?.emailNotificationEmails}
                helperText={
                  errors?.emailInfo?.emailNotificationEmails?.message || ''
                }
              />
            </Box>
          )}
        />
      </Stack>
      <Stack
        flexDirection={'row'}
        gap={1}
        maxWidth={'100%'}
        sx={{ overflowX: 'auto' }}
      >
        {emailNotificationEmails.length ? (
          emailNotificationEmails.map((user) => {
            return (
              <Box key={user}>
                <AvatarChip
                  email={user}
                  type={'Unknown'}
                  onDelete={() => {
                    setValue(
                      'emailInfo.emailNotificationEmails',
                      emailNotificationEmails.filter((email) => email !== user)
                    );
                  }}
                />
              </Box>
            );
          })
        ) : (
          <Box height={25} />
        )}
      </Stack>
      <Stack flexDirection={'row'} gap={1}>
        <Controller
          name={'emailInfo.ccEmails'}
          control={control}
          render={({ field }) => (
            <Box sx={{ width: 400 }}>
              <SharedAutocomplete
                label="Cc"
                sources={['users', 'groups']}
                autocompleteValue={addedCcEmail}
                setAutoCompleteValue={(val) => {
                  setAddedCcEmail(val);
                  if (val && !field.value?.includes(val)) {
                    const updatedUsers = [...(field.value || []), val];
                    setValue('emailInfo.ccEmails', updatedUsers, {
                      shouldDirty: true,
                    });
                    setAddedCcEmail('');
                    ccEmailNotificatinKey.current = new Date()
                      .getTime()
                      .toString();
                    trigger('emailInfo.ccEmails');
                    clearErrors('emailInfo.ccEmails');
                  }
                }}
                errors={!!errors.emailInfo?.ccEmails}
                helperText={errors?.emailInfo?.ccEmails?.message || ''}
              />
            </Box>
          )}
        />
      </Stack>
      <Stack
        flexDirection={'row'}
        gap={1}
        maxWidth={'100%'}
        sx={{ overflowX: 'auto' }}
      >
        {ccEmails?.length ? (
          ccEmails.map((user) => {
            return (
              <Box key={user}>
                <AvatarChip
                  email={user}
                  type={'Unknown'}
                  onDelete={() => {
                    setValue(
                      'emailInfo.ccEmails',
                      ccEmails.filter((email) => email !== user)
                    );
                  }}
                />
              </Box>
            );
          })
        ) : (
          <Box height={25} />
        )}
      </Stack>
      <Stack flexDirection={'row'} gap={1}>
        <Controller
          name={'emailInfo.bccEmails'}
          control={control}
          render={({ field }) => (
            <Box sx={{ width: 400 }}>
              <SharedAutocomplete
                label="Bcc"
                sources={['users', 'groups']}
                autocompleteValue={addedBccEmail}
                setAutoCompleteValue={(val) => {
                  setAddedBccEmail(val);
                  if (val && !field.value?.includes(val)) {
                    const updatedUsers = [...(field.value || []), val];
                    setValue('emailInfo.bccEmails', updatedUsers, {
                      shouldDirty: true,
                    });
                    setAddedBccEmail('');
                    bccEmailNotificatinKey.current = new Date()
                      .getTime()
                      .toString();
                    trigger('emailInfo.bccEmails');
                    clearErrors('emailInfo.bccEmails');
                  }
                }}
                errors={!!errors.emailInfo?.bccEmails}
                helperText={errors?.emailInfo?.bccEmails?.message || ''}
              />
            </Box>
          )}
        />
      </Stack>
      <Stack
        flexDirection={'row'}
        gap={1}
        maxWidth={'100%'}
        sx={{ overflowX: 'auto' }}
      >
        {bccEmails?.length ? (
          bccEmails.map((user) => {
            return (
              <Box key={user}>
                <AvatarChip
                  email={user}
                  type={'Unknown'}
                  onDelete={() => {
                    setValue(
                      'emailInfo.bccEmails',
                      bccEmails.filter((email) => email !== user)
                    );
                  }}
                />
              </Box>
            );
          })
        ) : (
          <Box height={25} />
        )}
      </Stack>
      <Stack>
        <Typography sx={{ fontWeight: 'bold' }}>Subject</Typography>
        {emailSubject?.includes('{|Password|}') && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.warning.main,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <WarningAmberOutlined
              sx={{ width: '16px', height: '18px', mr: 0.5 }}
            />{' '}
            Passwords cannot be resolved in rules engine
          </Typography>
        )}
      </Stack>
      <Box sx={{ height: 110 }}>
        <MergeTagOnlyEditor
          editorRef={editorSubjectRef}
          content={emailSubject}
          setContent={setSubject}
          placeholder="Type your email subject here..."
          mergeTags={mergeTags.map((tagGroup) => ({
            ...tagGroup,
            menu: tagGroup.menu?.filter(
              (item) => item.value !== '{|Password|}'
            ),
          }))}
        />
      </Box>
      <Stack>
        <Typography sx={{ fontWeight: 'bold' }}>Body</Typography>
        {htmlBody?.includes('{|Password|}') && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.warning.main,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <WarningAmberOutlined
              sx={{ width: '16px', height: '18px', mr: 0.5 }}
            />{' '}
            Passwords cannot be resolved in rules engine
          </Typography>
        )}
      </Stack>
      <Box sx={{ height: 200 }}>
        <DistractionFreeEditor
          editorRef={editorRef}
          content={htmlBody}
          setContent={setHtmlBody}
          placeholder="Type your email body here..."
          mergeTags={mergeTags}
        />
      </Box>
    </Stack>
  );
}
